import {
    Box,
    Button,
    FormControlLabel,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import ReplayIcon from '@mui/icons-material/Replay'
import { useCallback, useMemo, useState } from 'react'
import { LazyDatePicker } from '../../../date/pickers'
import dayjs, { Dayjs } from 'dayjs'
import { useSelector } from 'react-redux'
import { Exchange } from '../../../../proto/generated/common'
import { Security } from '../../../../proto/generated/security'
import { selectTradingCalendarDataByExchange } from '../../../../selectors/chart'
import { createStringDateFromStamp, strikeValues } from '../../../../util/tradePage'
import { getEffectiveTradingDate } from '../../../../util/tradingCalendar'
import { OpenInterestChartSettings } from '../../../chartV2/chart.typings'
import {
    chartSettingsGridRowSx,
    chartSettingsLabelSx,
    chartSettingsRowControlsSx,
    chartSettingsSectionTitleSx,
    chartSettingsTabPanelSx,
} from '../../../chartSettings/chartSettingsLayout'
import OiAnalysisExpiriesMenu from '../../../../pages/OpenInterestAnalysis/OiAnalysisExpiriesMenu'
import {
    OI_TIME_WINDOW_MINUTES,
    oiTimeWindowLabel,
} from '../../../../pages/OpenInterestAnalysis/OiAnalysisTimeSlider'
import {
    oiAnalysisDatePickerSlotProps,
    oiAnalysisDateTextFieldSx,
} from '../../../../pages/OpenInterestAnalysis/oiAnalysisControlsTheme'
import { SETTINGS_OVERLAY_Z_INDEX } from '../../widgetSettingsMenuContext'

type Props = {
    settings: OpenInterestChartSettings
    onSettingsPatch: (patch: Partial<OpenInterestChartSettings>) => void
    security?: Security
}

const OpenInterestSettingsPanel = ({ settings, onSettingsPatch, security }: Props) => {
    const [datePickerOpen, setDatePickerOpen] = useState(false)
    const calendarEvents = useSelector(
        selectTradingCalendarDataByExchange(security?.exchange ?? Exchange.NSE)
    )
    const effectiveTradingDate = useMemo(
        () => getEffectiveTradingDate(calendarEvents),
        [calendarEvents]
    )

    const selectedDate = settings.selectedDate
    const selectedExpiries = settings.selectedExpiries ?? []
    const strikeCount = settings.strikeCount ?? '25'
    const showOI = settings.showOI ?? false
    const replayKey = settings.replayKey ?? 0
    const expiriesMenuLoading = settings.expiriesMenuLoading ?? false
    const timeWindowMinutes = settings.timeWindowMinutes ?? 'full'

    const expiryOptions = useMemo(
        () =>
            (settings.expiriesMenuFromApi ?? []).map((value) => ({
                value,
                label: createStringDateFromStamp(Number(value)),
            })),
        [settings.expiriesMenuFromApi]
    )

    const handleDateChange = useCallback(
        (date: Dayjs | null) => {
            setDatePickerOpen(false)
            if (date) {
                onSettingsPatch({
                    selectedDate: date.startOf('day').valueOf(),
                    selectedExpiries: [],
                    expiriesMenuFromApi: [],
                    expiriesMenuLoading: true,
                })
            }
        },
        [onSettingsPatch]
    )

    const shouldDisableDate = useCallback(
        (day: Dayjs) => {
            if (day.isAfter(dayjs(), 'day')) {
                return true
            }
            const dayStr = day.format('YYYYMMDD')
            const isWeekend = day.day() === 0 || day.day() === 6
            const holidays = calendarEvents?.holidays.map((h) => h.date) ?? []
            const isHoliday = holidays.includes(dayStr)
            const special = calendarEvents?.specialTradingDays?.find((st) => st.date === dayStr)
            const isSpecialTradingDay = Boolean(special?.sessionTypes?.length)
            if (isSpecialTradingDay) {
                return false
            }
            return isWeekend || isHoliday
        },
        [calendarEvents]
    )

    return (
        <Box sx={{ ...chartSettingsTabPanelSx, minHeight: 280, p: 2 }}>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                SELECTION
            </Typography>

            <Box sx={{ ...chartSettingsGridRowSx, width: '100%' }}>
                <Typography sx={chartSettingsLabelSx}>Date</Typography>
                <Box sx={{ ...chartSettingsRowControlsSx, width: '100%' }}>
                    <>
                        <LazyDatePicker
                            disableOpenPicker
                            open={datePickerOpen}
                            onOpen={() => setDatePickerOpen(true)}
                            onClose={() => setDatePickerOpen(false)}
                            value={
                                selectedDate != null
                                    ? dayjs(selectedDate)
                                    : dayjs(effectiveTradingDate)
                            }
                            onChange={handleDateChange}
                            format="DD/MM/YYYY"
                            maxDate={dayjs()}
                            shouldDisableDate={shouldDisableDate}
                            slotProps={{
                                textField: {
                                    ...oiAnalysisDatePickerSlotProps.textField,
                                    id: 'open-interest-widget-date-picker',
                                    inputProps: { 'aria-label': 'Trading date' },
                                    onClick: () => setDatePickerOpen(true),
                                    sx: {
                                        ...oiAnalysisDateTextFieldSx,
                                        width: '140px',
                                        minWidth: '140px',
                                        maxWidth: '140px',
                                        cursor: 'pointer',
                                    },
                                },
                                desktopPaper: {
                                    sx: { zIndex: SETTINGS_OVERLAY_Z_INDEX },
                                },
                                popper: {
                                    sx: { zIndex: SETTINGS_OVERLAY_Z_INDEX },
                                },
                            }}
                        />
                    </>
                </Box>
            </Box>

            <Box sx={{ ...chartSettingsGridRowSx, width: '100%', alignItems: 'flex-start' }}>
                <Typography sx={{ ...chartSettingsLabelSx, pt: 0.75 }}>Expiries</Typography>
                <Box sx={{ ...chartSettingsRowControlsSx, width: '100%' }}>
                    <OiAnalysisExpiriesMenu
                        options={expiryOptions}
                        selected={selectedExpiries}
                        onChange={(next) => onSettingsPatch({ selectedExpiries: next })}
                        loading={expiriesMenuLoading}
                        idPrefix="open-interest-widget"
                    />
                </Box>
            </Box>

            <Box sx={{ ...chartSettingsGridRowSx, width: '100%' }}>
                <Typography sx={chartSettingsLabelSx}>Strikes</Typography>
                <Box sx={{ ...chartSettingsRowControlsSx, width: '100%' }}>
                    <ToggleButtonGroup
                        size="small"
                        value={strikeCount}
                        exclusive
                        onChange={(_, value) => value && onSettingsPatch({ strikeCount: value })}
                        sx={{ height: '32px' }}
                    >
                        {strikeValues.values.map((s) => (
                            <ToggleButton key={s.value} value={s.value} sx={{ minWidth: '40px', px: 1 }}>
                                {s.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Box sx={{ ...chartSettingsGridRowSx, width: '100%', alignItems: 'flex-start' }}>
                <Typography sx={{ ...chartSettingsLabelSx, pt: 0.75 }}>Time</Typography>
                <Box sx={{ ...chartSettingsRowControlsSx, width: '100%', flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        size="small"
                        value={String(timeWindowMinutes)}
                        exclusive
                        onChange={(_, value) => {
                            if (!value) return
                            onSettingsPatch({
                                timeWindowMinutes: value === 'full' ? 'full' : Number(value),
                            })
                        }}
                        sx={{ flexWrap: 'wrap' }}
                    >
                        {OI_TIME_WINDOW_MINUTES.map((mins) => (
                            <ToggleButton key={mins} value={String(mins)} sx={{ px: 1, textTransform: 'none' }}>
                                {oiTimeWindowLabel(mins)}
                            </ToggleButton>
                        ))}
                        <ToggleButton value="full" sx={{ px: 1, textTransform: 'none' }}>
                            Full Day
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ ...chartSettingsSectionTitleSx, mt: 2 }}>
                DISPLAY
            </Typography>

            <Box sx={{ ...chartSettingsGridRowSx, width: '100%' }}>
                <Typography sx={chartSettingsLabelSx}>Show Changes</Typography>
                <Box sx={{ ...chartSettingsRowControlsSx, width: '100%' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showOI}
                                onChange={(e) => onSettingsPatch({ showOI: e.target.checked })}
                                color="primary"
                            />
                        }
                        label=""
                        sx={{ m: 0 }}
                    />
                </Box>
            </Box>

            <Box sx={{ ...chartSettingsGridRowSx, width: '100%' }}>
                <Typography sx={chartSettingsLabelSx}>Refresh</Typography>
                <Box sx={{ ...chartSettingsRowControlsSx, width: '100%' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ReplayIcon />}
                        onClick={() => onSettingsPatch({ replayKey: replayKey + 1 })}
                        disabled={!showOI}
                        sx={{ textTransform: 'none' }}
                    >
                        Refresh Changes
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}

export default OpenInterestSettingsPanel
